import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Eye, EyeOff, ArrowUp, SlidersHorizontal } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import OceanAssessment from '../components/ocean/OceanAssessment';
import HeroSection from '../components/dashboard/HeroSection';
import WelcomeCard from '../components/dashboard/WelcomeCard';
import MyAvAsCard from '../components/dashboard/MyAvAsCard';
import RecentConversationsCard from '../components/dashboard/RecentConversationsCard';
import MoodTrackerCard from '../components/dashboard/MoodTrackerCard';
import CurrentStreakCard from '../components/dashboard/CurrentStreakCard';
import BadgesCard from '../components/dashboard/BadgesCard';
import ActivityFeedCard from '../components/dashboard/ActivityFeedCard';
import NotificationsCard from '../components/dashboard/NotificationsCard';
import SocialConnectionsCard from '../components/dashboard/SocialConnectionsCard';
import SocialScoreCard from '../components/dashboard/SocialScoreCard';
import RealWorldChallengeCard from '../components/dashboard/RealWorldChallengeCard';
import PathwaysBadgeCard from '../components/dashboard/PathwaysBadgeCard';
import PausedOverlay from '../components/dashboard/PausedOverlay';
import ReadinessScoreCard from '../components/dashboard/ReadinessScoreCard';
import AffirmationCard from '../components/dashboard/AffirmationCard';
import MoodInsightsCard from '../components/dashboard/MoodInsightsCard';
import AvAEvolutionCard from '../components/dashboard/AvAEvolutionCard';
import ConfidenceReplayCard from '../components/dashboard/ConfidenceReplayCard';
import QuestOfTheWeekCard from '../components/dashboard/QuestOfTheWeekCard';
import MoodStreakGardenCard from '../components/dashboard/MoodStreakGardenCard';
import AvaMissesYouCard from '../components/dashboard/AvaMissesYouCard';
import ConversationFlashbackCard from '../components/dashboard/ConversationFlashbackCard';
import StreakCard from '../components/engagement/StreakCard';
import FortuneCookie from '../components/engagement/FortuneCookie';
import MilestoneCelebration from '../components/engagement/MilestoneCelebration';
import FriendChallengeCard from '../components/dashboard/FriendChallengeCard';
import OnboardingTour from '../components/onboarding/OnboardingTour';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

// ──────────────────────────────────────────────────────────────────────
// CORPORATE TEAM MEMBER DASHBOARD
// Duplicate of the User Dashboard with corporate-inappropriate features
// removed: Subscription, Memorial AvAs, Family Charter, Hearts Staking,
// Family Challenge, Limited NFT Drops, Surprise Drop Modal.
// ──────────────────────────────────────────────────────────────────────

// Fixed cards — always shown, cannot be removed
const FIXED_CARDS = [
  { id: 'my-avas', component: MyAvAsCard, title: 'My Trainers' },
  { id: 'ava-evolution', component: AvAEvolutionCard, title: 'AvA Evolution' },
  { id: 'quest-of-the-week', component: QuestOfTheWeekCard, title: 'Quest of the Week' },
  { id: 'real-world-challenge', component: RealWorldChallengeCard, title: "Today's Challenge" },
  { id: 'recent-conversations', component: RecentConversationsCard, title: 'Recent Conversations' },
  { id: 'mood-tracker', component: MoodTrackerCard, title: 'Mood Tracker' },
  { id: 'mood-streak-garden', component: MoodStreakGardenCard, title: 'Mood Garden' },
];

// Optional cards — school-appropriate only (no subscription, memorial, family, staking, NFT drops)
const OPTIONAL_CARDS = {
  'pathways': { component: PathwaysBadgeCard, title: 'My Badge Pathways', description: 'Track your badge progress and mini-journeys' },
  'current-streak': { component: CurrentStreakCard, title: 'Current Streak', description: 'See your daily practice streak' },
  'badges': { component: BadgesCard, title: 'Celebrations', description: 'View earned badges and achievements' },
  'social-score': { component: SocialScoreCard, title: 'Social Score', description: 'Monitor your social engagement score' },
  'social-connections': { component: SocialConnectionsCard, title: 'Real Connections', description: 'Track your real-world social connections' },
  'activity-feed': { component: ActivityFeedCard, title: 'Activity Feed', description: 'See recent activity across the platform' },
  'notifications': { component: NotificationsCard, title: 'Notifications', description: 'View your pending notifications' },
  'mood-insights': { component: MoodInsightsCard, title: 'Mood Insights', description: 'AI-powered analysis of your mood patterns' },
  'confidence-replay': { component: ConfidenceReplayCard, title: 'Confidence Replay', description: 'Your best session highlights — shareable!' },
  'conversation-flashback': { component: ConversationFlashbackCard, title: 'Flashbacks', description: 'On This Day — see how much you\'ve grown' },
};

const CorporateTeamMemberDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [enabledOptional, setEnabledOptional] = useState([]);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showOcean, setShowOcean] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    loadPreferences();
    apiClient.request('/api/users/subscription/status')
      .then(data => { if (data.subscription_status === 'paused') setIsPaused(true); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const checkOcean = async () => {
      try {
        const res = await apiClient.request('/api/ocean/status');
        if (res.should_trigger) setShowOcean(true);
      } catch { /* silent */ }
    };
    checkOcean();
  }, []);

  useEffect(() => {
    const checkNewUser = async () => {
      try {
        // Feb 2026 — was /api/conversations (404). Sessions list is the
        // canonical "has the user started practising?" signal.
        const res = await apiClient.request('/api/sessions');
        if (!res || (Array.isArray(res) && res.length === 0)) {
          setIsNewUser(true);
        }
      } catch {}
    };
    checkNewUser();
  }, []);

  const loadPreferences = async () => {
    try {
      const prefs = await apiClient.getDashboardPreferences();
      if (prefs.enabled_optional && prefs.enabled_optional.length > 0) {
        const validCards = prefs.enabled_optional.filter(id => OPTIONAL_CARDS[id]);
        setEnabledOptional(validCards);
      }
    } catch {}
  };

  const savePreferences = useCallback(async (newEnabled) => {
    try {
      await apiClient.saveDashboardPreferences({
        card_order: FIXED_CARDS.map(c => c.id),
        hidden_cards: [],
        enabled_optional: newEnabled,
        theme: 'default'
      });
    } catch {
      console.error('Failed to save preferences');
    }
  }, []);

  const toggleOptionalCard = (cardId) => {
    setEnabledOptional(prev => {
      const newList = prev.includes(cardId)
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId];
      savePreferences(newList);
      return newList;
    });
  };

  return (
    <DashboardLayout>
      {isPaused && <PausedOverlay />}

      {showOcean && (
        <OceanAssessment
          onComplete={() => { setShowOcean(false); navigate('/profile'); }}
          onPostpone={() => setShowOcean(false)}
        />
      )}

      <HeroSection onCustomize={() => setShowAddPanel(true)} />

      <div className="flex items-center justify-between mb-4 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
        <h2 className="text-lg font-bold text-gray-800">Your Dashboard</h2>
        <button
          onClick={() => setShowAddPanel(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all hover:shadow-sm"
          data-testid="customize-dashboard-btn"
        >
          <SlidersHorizontal size={16} />
          Customize
        </button>
      </div>

      {isNewUser && (
        <div className="mb-6">
          <WelcomeCard />
        </div>
      )}

      <MilestoneCelebration />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StreakCard />
        <FortuneCookie />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <FriendChallengeCard />
        <AvaMissesYouCard />
      </div>

      <div className="mb-6">
        <AffirmationCard />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <ReadinessScoreCard />
        <MoodInsightsCard />
      </div>

      <div className="content-grid" data-testid="fixed-cards-grid">
        <div className="grid-column">
          {FIXED_CARDS.filter((_, i) => i % 3 === 0).map((card, idx) => (
            <div key={card.id} className="animate-slideUp" style={{ animationDelay: `${idx * 0.08}s` }}>
              <card.component />
            </div>
          ))}
        </div>
        <div className="grid-column">
          {FIXED_CARDS.filter((_, i) => i % 3 === 1).map((card, idx) => (
            <div key={card.id} className="animate-slideUp" style={{ animationDelay: `${(idx * 0.08) + 0.04}s` }}>
              <card.component />
            </div>
          ))}
        </div>
        <div className="grid-column">
          {FIXED_CARDS.filter((_, i) => i % 3 === 2).map((card, idx) => (
            <div key={card.id} className="animate-slideUp" style={{ animationDelay: `${(idx * 0.08) + 0.08}s` }}>
              <card.component />
            </div>
          ))}
        </div>
      </div>

      {enabledOptional.length > 0 && (
        <div className="content-grid mt-6" data-testid="optional-cards-grid">
          <div className="grid-column">
            {enabledOptional.filter((_, i) => i % 3 === 0).map((cardId, idx) => {
              const config = OPTIONAL_CARDS[cardId];
              if (!config) return null;
              const CardComponent = config.component;
              return (
                <div key={cardId} className="relative group animate-slideUp" style={{ animationDelay: `${idx * 0.06}s` }}>
                  <button
                    onClick={() => toggleOptionalCard(cardId)}
                    className="absolute -top-2 -right-2 z-10 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                    data-testid={`remove-card-${cardId}`}
                    title="Remove from dashboard"
                  >
                    <X size={14} />
                  </button>
                  <CardComponent />
                </div>
              );
            })}
          </div>
          <div className="grid-column">
            {enabledOptional.filter((_, i) => i % 3 === 1).map((cardId, idx) => {
              const config = OPTIONAL_CARDS[cardId];
              if (!config) return null;
              const CardComponent = config.component;
              return (
                <div key={cardId} className="relative group animate-slideUp" style={{ animationDelay: `${(idx * 0.06) + 0.03}s` }}>
                  <button
                    onClick={() => toggleOptionalCard(cardId)}
                    className="absolute -top-2 -right-2 z-10 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                    data-testid={`remove-card-${cardId}`}
                    title="Remove from dashboard"
                  >
                    <X size={14} />
                  </button>
                  <CardComponent />
                </div>
              );
            })}
          </div>
          <div className="grid-column">
            {enabledOptional.filter((_, i) => i % 3 === 2).map((cardId, idx) => {
              const config = OPTIONAL_CARDS[cardId];
              if (!config) return null;
              const CardComponent = config.component;
              return (
                <div key={cardId} className="relative group animate-slideUp" style={{ animationDelay: `${(idx * 0.06) + 0.06}s` }}>
                  <button
                    onClick={() => toggleOptionalCard(cardId)}
                    className="absolute -top-2 -right-2 z-10 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                    data-testid={`remove-card-${cardId}`}
                    title="Remove from dashboard"
                  >
                    <X size={14} />
                  </button>
                  <CardComponent />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="h-8" />

      {showAddPanel && (
        <div className="fixed inset-0 z-50" data-testid="add-cards-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowAddPanel(false)} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-gray-900">Add Cards</h2>
              <button onClick={() => setShowAddPanel(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <p className="text-sm text-gray-500 mb-4">Toggle cards to show or hide them on your dashboard</p>
              <div className="space-y-2">
                {Object.entries(OPTIONAL_CARDS).map(([cardId, config]) => {
                  const isEnabled = enabledOptional.includes(cardId);
                  return (
                    <button
                      key={cardId}
                      onClick={() => toggleOptionalCard(cardId)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${
                        isEnabled
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      data-testid={`toggle-card-${cardId}`}
                    >
                      <div className="min-w-0">
                        <p className={`font-semibold text-sm ${isEnabled ? 'text-blue-700' : 'text-gray-900'}`}>{config.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{config.description}</p>
                      </div>
                      <div className={`flex-shrink-0 ml-3 p-1.5 rounded-lg ${isEnabled ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {isEnabled ? <Eye size={16} /> : <EyeOff size={16} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowAddPanel(false)}
                className="w-full py-2.5 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-11 h-11 bg-gray-900 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-700 transition-all z-40"
          data-testid="scroll-to-top"
          aria-label="Scroll to top"
        >
          <ArrowUp size={20} />
        </button>
      )}

      <OnboardingTour user={user} />
    </DashboardLayout>
  );
};

export default CorporateTeamMemberDashboard;
